import { ExclamationCircleOutlined } from "@ant-design/icons"
import { Flex, Modal, Skeleton, Typography, message } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { type BlockerFunction, useNavigate, useParams } from "react-router-dom"

import { neutral } from "../../../../components/widgets/tokens.ts"
import { generateDocumentTitle } from "../../../../constants/document-title.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import usePrompt from "../../../../hooks/use-prompt.ts"
import { getEntityName } from "../../../../utils/user/get-entity-name.ts"
import { getReportDetails, updateReport } from "../../api/services.ts"
import type { CreateReportData } from "../../api/services.types.ts"
import { normalizeDefinition } from "../../helpers/reportDefinition.ts"
import { distinctFolderNames } from "../../helpers/reportFolders.ts"
import {
  DEFAULT_REPORT_ICON,
  reportIconKeyOf
} from "../../helpers/reportIcons.tsx"
import {
  buildReportSettingsPatch,
  type ReportSettingsPatch
} from "../../helpers/reportSettings.ts"
import type { ShareEditorEntry } from "../../helpers/reportSharing.ts"
import { useReportCanvasAutosave } from "../../hooks/useReportCanvasAutosave.ts"
import {
  reportCanvasHydrate,
  reportCanvasReset
} from "../../redux/reportCanvas.slice.ts"
import { fetchReportCatalog } from "../../redux/reportCanvas.thunks.ts"
import {
  createReport,
  deleteReport,
  fetchReports,
  toggleReportFavorite
} from "../../redux/reports.thunks.ts"
import type { ReportDefinitionV1 } from "../../types/canvas.types.ts"
import type { ReportDetail, ReportVisibility } from "../../types/types.ts"
import { ReportBuildLayout } from "../build/ReportBuildLayout.tsx"
import { ReportFiltersStep } from "../filters/ReportFiltersStep.tsx"
import {
  ReportWizardActions,
  ReportWizardStepper,
  type WizardStep
} from "./ReportWizardHeader.tsx"
import {
  ReportWizardStepGeneral,
  type WizardGeneralInfo
} from "./ReportWizardStepGeneral.tsx"
import {
  ReportWizardStepShare,
  type ReportWizardShareHandle
} from "./ReportWizardStepShare.tsx"

const { Title, Text } = Typography

const emptyDefinition = (icon: string): ReportDefinitionV1 => ({
  version: 1,
  widgets: [],
  layout: [],
  filters: {},
  icon
})

const INITIAL_GENERAL: WizardGeneralInfo = {
  name: "",
  description: "",
  tags: [],
  folder: "",
  favorite: false,
  visibility: "private",
  icon: DEFAULT_REPORT_ICON
}

const ReportWizardPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { id: resumeId } = useParams<{ id: string }>()
  const catalogStatus = useAppSelector((s) => s.reportCanvas.catalogStatus)
  const reports = useAppSelector((s) => s.reports.results)
  const userInfo = useAppSelector((s) => s.auth.userInfo)
  const folderOptions = distinctFolderNames(reports)

  const [step, setStep] = useState<WizardStep>(1)
  const [completed, setCompleted] = useState<Set<WizardStep>>(new Set())
  const [general, setGeneral] = useState<WizardGeneralInfo>(INITIAL_GENERAL)
  const [nameError, setNameError] = useState(false)
  const [busy, setBusy] = useState(false)
  const [created, setCreated] = useState<ReportDetail | null>(null)
  const [shareEntries, setShareEntries] = useState<ShareEditorEntry[] | null>(
    null
  )
  const shareRef = useRef<ReportWizardShareHandle>(null)
  const finishing = useRef(false)
  const resuming = Boolean(resumeId) && !created

  const { saveNow, cancel: cancelAutosave } = useReportCanvasAutosave(
    created?.id,
    false
  )

  // Step 1 already POSTed the report, so *any* exit that isn't a finish strands
  // it — the Cancel button, the sidebar, browser Back and a reload alike.
  const blockExit = useCallback<BlockerFunction>(
    (args) => {
      if (!created || finishing.current) return false
      const next = args?.nextLocation
      if (!next) return true
      return !next.pathname.startsWith(RoutesPaths.reports.create)
    },
    [created]
  )

  const blocker = usePrompt(blockExit)
  const discardOpen = blocker.state === "blocked"

  useEffect(() => {
    generateDocumentTitle(t("reports.wizard.title"))
  }, [t])

  useEffect(() => {
    if (catalogStatus === "idle") void dispatch(fetchReportCatalog())
  }, [dispatch, catalogStatus])

  useEffect(() => {
    if (reports.length === 0) void dispatch(fetchReports({}))
  }, [dispatch, reports.length])

  useEffect(() => () => void dispatch(reportCanvasReset()), [dispatch])

  // A refresh at step 2+ lands back here with the created report id in the
  // route; re-fetch it and resume at Build instead of orphaning the report and
  // POSTing a duplicate on the next advance.
  //
  // Deliberately NOT depending on dispatch/navigate/t: this effect's only
  // reactive trigger is "which report are we resuming, and have we already
  // got it" — dispatch and navigate are stable by their own contracts, and t
  // is read only for a static translation key inside the async callback, so
  // neither needs to be fresh. Including them would let a dependency that is
  // merely *supposed* to be stable (as this component's own tests prove by
  // mocking useTranslation with a fresh `t` every render) re-enter this
  // effect — which starts a new fetch while the previous one is still
  // in-flight, and React tearing down that previous effect's cleanup (which
  // every re-run does, regardless of why) cancels it before it can commit,
  // so the resume loops forever without ever setting `created`.
  useEffect(() => {
    if (!resumeId || created) return
    let cancelled = false
    void (async () => {
      const response = await getReportDetails(resumeId)
      if (cancelled) return
      if (!response.ok || !response.data) {
        if (response.status === 404)
          message.error(t("reports.wizard.draftExpired"))
        navigate(RoutesPaths.reports.list, { replace: true })
        return
      }
      const detail = response.data as ReportDetail
      if (!detail.is_draft) {
        navigate(RoutesPaths.reports.view(detail.id), { replace: true })
        return
      }
      // An empty PATCH still reaches the model's save(), which bumps `updated`
      // and so keeps the draft out of the cleanup task's reach.
      void updateReport(resumeId, {})
      const definition = normalizeDefinition(detail.definition)
      setCreated(detail)
      setGeneral({
        name: detail.name,
        description: detail.description ?? "",
        tags: detail.tags ?? [],
        folder: detail.folder ?? "",
        favorite: detail.is_favorite,
        visibility: detail.visibility,
        icon: reportIconKeyOf(definition)
      })
      dispatch(reportCanvasHydrate({ reportId: detail.id, definition }))
      setCompleted(new Set<WizardStep>([1]))
      setStep(2)
    })()
    return () => {
      cancelled = true
    }
  }, [resumeId, created])

  const markCompleted = (n: WizardStep) =>
    setCompleted((prev) => new Set(prev).add(n))

  const handleGeneralChange = (patch: Partial<WizardGeneralInfo>) => {
    if (
      patch.visibility !== undefined &&
      patch.visibility !== general.visibility &&
      shareEntries !== null
    ) {
      // Emptied, not discarded: the Share step re-fetches while its list is
      // absent, and would restore what this just cleared.
      setShareEntries([])
    }
    setGeneral((prev) => ({ ...prev, ...patch }))
    if (patch.name !== undefined && patch.name.trim()) setNameError(false)
  }

  const createFromGeneral = async (): Promise<boolean> => {
    if (!general.name.trim()) {
      setNameError(true)
      return false
    }
    setBusy(true)
    try {
      const payload: CreateReportData = {
        name: general.name.trim(),
        description: general.description.trim() || undefined,
        tags: general.tags,
        visibility: general.visibility,
        favorite: general.favorite,
        folder: general.folder || undefined,
        definition: emptyDefinition(general.icon),
        is_draft: true
      }
      const action = await dispatch(createReport(payload))
      if (!createReport.fulfilled.match(action)) return false
      const detail = action.payload?.details?.data as ReportDetail | undefined
      if (!detail) return false
      setCreated(detail)
      dispatch(
        reportCanvasHydrate({
          reportId: detail.id,
          definition: normalizeDefinition(detail.definition)
        })
      )
      // Encode the created id in the URL so a refresh resumes this report.
      navigate(RoutesPaths.reports.createResume(detail.id), { replace: true })
      return true
    } finally {
      setBusy(false)
    }
  }

  const persistGeneralEdits = async (): Promise<boolean> => {
    if (!created) return true
    if (!general.name.trim()) {
      setNameError(true)
      return false
    }
    setBusy(true)
    try {
      const patch: ReportSettingsPatch & { visibility?: ReportVisibility } =
        buildReportSettingsPatch(created, {
          name: general.name,
          description: general.description,
          folder: general.folder,
          tags: general.tags
        })
      if (general.visibility !== created.visibility)
        patch.visibility = general.visibility

      if (Object.keys(patch).length > 0) {
        const response = await updateReport(created.id, patch)
        if (!response.ok) return false
        setCreated((prev) => (prev ? { ...prev, ...patch } : prev))
      }
      if (general.favorite !== created.is_favorite) {
        const action = await dispatch(
          toggleReportFavorite({
            id: created.id,
            previousIsFavorite: created.is_favorite
          })
        )
        if (toggleReportFavorite.fulfilled.match(action))
          setCreated((prev) =>
            prev ? { ...prev, is_favorite: general.favorite } : prev
          )
      }
      return true
    } finally {
      setBusy(false)
    }
  }

  const handleNext = async () => {
    if (resuming) return
    if (step === 1) {
      const ok = created
        ? await persistGeneralEdits()
        : await createFromGeneral()
      if (!ok) return
      markCompleted(1)
      setStep(2)
      return
    }
    if (step === 2) {
      markCompleted(2)
      setStep(3)
      return
    }
    if (step === 3) {
      markCompleted(3)
      setStep(4)
      return
    }
    if (!created) return
    setBusy(true)
    try {
      const { ok: canvasSaved } = await saveNow()
      if (!canvasSaved) {
        message.error(t("reports.wizard.finishSaveError"))
        return
      }
      if (shareRef.current) {
        const ok = await shareRef.current.save()
        if (!ok) return
      }
      // Finalize last: any earlier failure must leave the report a draft, so the
      // discard guard stays correct instead of offering to delete a live report.
      const response = await updateReport(created.id, { is_draft: false })
      if (!response.ok) {
        message.error(t("reports.wizard.finishError"))
        return
      }
      finishing.current = true
      navigate(RoutesPaths.reports.view(created.id))
    } finally {
      setBusy(false)
    }
  }

  const handleBack = () => setStep((s) => (s > 1 ? ((s - 1) as WizardStep) : s))

  const handleCancel = () => navigate(RoutesPaths.reports.list)

  const discardAndLeave = async () => {
    if (!created) return
    setBusy(true)
    try {
      cancelAutosave()
      const action = await dispatch(deleteReport({ id: created.id }))
      if (!deleteReport.fulfilled.match(action)) {
        blocker.reset?.()
        return
      }
      finishing.current = true
      blocker.proceed?.()
    } finally {
      setBusy(false)
    }
  }

  const nextLabel =
    step === 1
      ? t("reports.wizard.nextBuild")
      : step === 2
        ? t("reports.wizard.nextFilters")
        : step === 3
          ? t("reports.wizard.nextShare")
          : t("reports.wizard.finish")

  const footerNote =
    step === 1
      ? t("reports.wizard.info.afterNext")
      : step === 3
        ? t("reports.wizard.tip.filtersLater")
        : ""

  return (
    <div className="grid size-full">
      <Flex
        vertical
        gap="middle"
        className="max-h-[calc(100vh-88px)] overflow-hidden p-1 -m-1"
      >
        <Flex align="center" justify="space-between" gap="middle" wrap="wrap">
          <Title level={4} className="m-0">
            {t("reports.wizard.title")}
          </Title>
          <div className="min-w-0 flex-1">
            <ReportWizardStepper current={step} completed={completed} />
          </div>
          <ReportWizardActions
            current={step}
            nextLabel={nextLabel}
            nextLoading={busy}
            onCancel={handleCancel}
            onBack={handleBack}
            onNext={handleNext}
          />
        </Flex>

        <div className="min-h-0 flex-1 overflow-auto">
          {resuming && <Skeleton active paragraph={{ rows: 6 }} />}
          {!resuming && step === 1 && (
            <ReportWizardStepGeneral
              value={general}
              onChange={handleGeneralChange}
              folderOptions={folderOptions}
              nameError={nameError}
              ownerName={getEntityName(created ? created.owner : userInfo)}
              savedVisibility={
                created?.visibility ?? INITIAL_GENERAL.visibility
              }
            />
          )}
          {step === 2 && created && <ReportBuildLayout />}
          {step === 3 && created && <ReportFiltersStep />}
          {step === 4 && created && (
            <ReportWizardStepShare
              ref={shareRef}
              reportId={created.id}
              owner={created.owner}
              visibility={general.visibility}
              entries={shareEntries}
              onEntriesChange={setShareEntries}
            />
          )}
        </div>

        {footerNote && (
          <Flex
            style={{
              borderTop: `1px solid ${neutral.border}`,
              paddingTop: 12
            }}
          >
            <Text type="secondary" style={{ fontSize: 13 }}>
              {footerNote}
            </Text>
          </Flex>
        )}
      </Flex>

      <Modal
        open={discardOpen}
        title={
          <span>
            <ExclamationCircleOutlined className="mr-2 text-amber-500" />
            {t("reports.wizard.discardConfirm.title")}
          </span>
        }
        okText={t("reports.wizard.discardConfirm.confirm")}
        okButtonProps={{ danger: true, loading: busy }}
        cancelText={t("reports.wizard.discardConfirm.keepEditing")}
        onOk={discardAndLeave}
        onCancel={() => blocker.reset?.()}
      >
        {t("reports.wizard.discardConfirm.body", { name: created?.name })}
      </Modal>
    </div>
  )
}

export default ReportWizardPage
